import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttributeProof, useOrderMilestones, useRecordRefund } from '../../api/hooks';
import { ApiError } from '../../api/client';
import type { ConsignmentInvoiceRow } from '../../api/types';
import { Alert, Button, Field, Money, Panel, Table } from '../../ui';
import { MilestoneSummary } from './MilestoneSummary';

/**
 * Admin order page — milestones, who each payment is for, and the refund of a cancelled order.
 *
 * The existing "Payment proofs" panel keeps Verify / Reject exactly as they were; this adds the
 * figures, an invoice picker for a PENDING proof (a decided proof never moves, and the API says
 * so), and "Record refund", which exists only on a CANCELLED order with verified money.
 * Nothing here is a gate: the API enforces every stop.
 */
export function MilestonesPanel(
  { orderId, orderStatus, invoices, canVerify, canRefund }: {
    orderId: string;
    orderStatus: string;
    invoices: ConsignmentInvoiceRow[];
    canVerify: boolean;
    canRefund: boolean;
  },
) {
  const { t } = useTranslation();
  const { data: m } = useOrderMilestones(orderId);
  const attribute = useAttributeProof(orderId);
  const refund = useRecordRefund(orderId);
  const [failed, setFailed] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  if (!m || !m.scheduled) return m ? <MilestoneSummary milestones={m} quietWhenLegacy /> : null;

  async function run(fn: () => Promise<unknown>) {
    setFailed(null);
    try { await fn(); } catch (e) { setFailed(e instanceof ApiError ? e.message : t('order.milestones.attributeFailed')); }
  }

  const live = invoices.filter((i) => i.state !== 'CANCELLED');
  const refName = (id: string | null) => (id ? live.find((i) => i.id === id)?.reference ?? id : null);
  const verified = m.verified !== '0.0000' && m.verified !== '0';

  return (
    <>
      <MilestoneSummary milestones={m} />
      {failed ? <Alert tone="stop">{failed}</Alert> : null}

      {live.length > 0 ? (
        <Panel title={t('order.milestones.forInvoice')}>
          <Table
            rows={m.proofs}
            rowKey={(p) => p.id}
            columns={[
              { header: t('order.milestones.colAmount'), numeric: true, render: (p) => <Money amount={p.amount} currency={m.currency} /> },
              { header: t('order.milestones.colStatus'), render: (p) => p.status },
              {
                header: t('order.milestones.forInvoice'),
                /* `consignmentInvoiceId` is the WIRE field name and is frozen in this release — it is
                   what the API answers with and what the browser sends. `consignmentInvoiceId` is
                   the agreed successor and moves with its ~110 call sites in D97 Wave 3, in one
                   commit (expand, deploy, contract). */
                render: (p) => (p.status === 'PENDING' && canVerify ? (
                  <select
                    className="mk-fld"
                    value={p.consignmentInvoiceId ?? ''}
                    disabled={attribute.isPending}
                    onChange={(e) => void run(() => attribute.mutateAsync({
                      proofId: p.id, consignmentInvoiceId: e.target.value === '' ? null : e.target.value,
                    }))}
                  >
                    <option value="">{t('order.milestones.orderLevel')}</option>
                    {live.map((i) => <option key={i.id} value={i.id}>{i.reference}</option>)}
                  </select>
                ) : (refName(p.consignmentInvoiceId) ?? t('order.milestones.orderLevel'))),
              },
            ]}
          />
        </Panel>
      ) : null}

      {orderStatus === 'CANCELLED' && verified && canRefund ? (
        <Panel title={t('order.milestones.refundTitle')}>
          {!refunding ? (
            <div style={{ padding: 16 }}>
              <Button tone="ghost" onClick={() => setRefunding(true)}>{t('order.milestones.refundAction')}</Button>
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              <Field label={t('order.milestones.refundReason')}>
                <input className="mk-fld" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <p>{t('order.milestones.refundConfirm', { amount: `${m.currency} ${m.verified}` })}</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button
                  disabled={reason.trim().length < 3 || refund.isPending}
                  onClick={() => void run(() => refund.mutateAsync({ reason: reason.trim() }))
                    .then(() => { setRefunding(false); setReason(''); })}
                >
                  {t('order.milestones.refundAction')}
                </Button>
                <Button tone="ghost" onClick={() => { setRefunding(false); setReason(''); }}>{t('common.cancel')}</Button>
              </div>
            </div>
          )}
        </Panel>
      ) : null}
    </>
  );
}
